import React, { useState, useEffect } from "react";
import Header from "./Header";
import SubHeader from "./SubHeader";
import DataTable, { DEFAULT_COLUMNS, formatNum } from "./DataTable";
import TableLoader from "../TableLoader";
import Apiauth from "../../../API/Apiauth";
import BriefSummaryCharts from "./graph/Briefsummarycharts";
import { toFormatPrice } from "../helpers/function";

function mapRows(apiData = []) {
  return apiData.map((row) => ({
    location: row.location_Name,
    target: row.target_Amount,
    sales: row.actual_Sales,
    _salesPct: `${row.achievement_pct}%`,
    _salesNeg: row.achievement_pct < 100,
    cost: row.actual_Cost,
    profit: row.actual_Profit,
    qty: row.actual_Qty,
    noOfInvoice: row.actual_Invoice_Count,
    noOfCustomer: row.actual_Customer_Count,
    newCustomer: row.actual_New_Customer,
  }));
}

function mapTotals(apiTotals = {}) {
  return {
    target: apiTotals.target_Amount,
    sales: apiTotals.actual_Sales,
    cost: apiTotals.actual_Cost,
    profit: apiTotals.actual_Profit,
    qty: apiTotals.actual_Qty,
    noOfInvoice: apiTotals.actual_Invoice_Count,
    noOfCustomer: apiTotals.actual_Customer_Count,
    newCustomer: apiTotals.actual_New_Customer,
  };
}

function mapMetrics(summary = {}) {
  return [
    { label: "Sales", value: toFormatPrice(Number(summary.total_Sales || 0)) },
    { label: "% Achievement", value: `${summary.achievement_pct ?? 0}%` },
    { label: "Profit", value: toFormatPrice(Number(summary.total_Profit || 0)) },
    { label: "New Customer", value: formatNum(summary.total_New_Customer) },
  ];
}

const BRIEF_COLUMNS = DEFAULT_COLUMNS.map((col) =>
  ["target", "sales", "cost", "profit"].includes(col.key)
    ? { ...col, isPriceColumn: true }
    : col,
);

const DEFAULT_PAGE_SIZE = 10;
const CHART_MAX_LIMIT = 100000;

const BriefSummary = () => {
  const [rows, setRows] = useState([]);
  const [chartRows, setChartRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({});
  const [appliedFilters, setAppliedFilters] = useState({});
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [showCharts, setShowCharts] = useState(false);

  const fetchBriefReport = ({
    page: p = 1,
    limit: l = pageSize,
    ...rest
  } = {}) => {
    setLoading(true);
    Apiauth({ page: p, limit: l, ...rest }, "Inventory/reports/briefReport")
      .then((res) => {
        if (res?.code === 200) {
          setRows(mapRows(res.data));
          setTotals(mapTotals(res.totals));
          setMetrics(mapMetrics(res.summary));
          setFilter(res.filters);
          setTotalRecords(res?.pagination?.total ?? 0);
        }
      })
      .finally(() => setLoading(false));
  };

  const fetchChartReport = (filters = {}) => {
    setLoading(true);
    Apiauth(
      { page: 1, limit: CHART_MAX_LIMIT, ...filters },
      "Inventory/reports/briefReport"
    )
      .then((res) => {
        if (res?.code === 200) {
          setChartRows(mapRows(res.data));
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchBriefReport({ page: 1, limit: DEFAULT_PAGE_SIZE });
  }, []);

  useEffect(() => {
    if (showCharts) {
      fetchChartReport(appliedFilters);
    }
  }, [showCharts, appliedFilters]);

  function handleFetchData({ page: newPage, limit: newLimit }) {
    if (newLimit !== pageSize) setPageSize(newLimit);
    setPage(newPage);
    fetchBriefReport({ ...appliedFilters, page: newPage, limit: newLimit });
  }

  function handleFilterApply(filterParams = {}) {
    setAppliedFilters(filterParams);
    setPage(1);
    fetchBriefReport({ ...filterParams, page: 1, limit: pageSize });
  }

  return (
    <>
      <Header title="Brief Summary" metrics={metrics} />
      <SubHeader filters={filter} showCharts={showCharts}  setShowCharts={setShowCharts} onFilterApply={handleFilterApply}/>

      {loading ? (
        <TableLoader />
      ) : (
        showCharts?(<BriefSummaryCharts  rows={chartRows}/>):
        (<DataTable
          columns={BRIEF_COLUMNS}
          data={rows}
          totals={totals}
          showSearch={true}
          showDateRange={true}
          pageLimit={pageSize}
          totalRecords={totalRecords}
          currentPage={page}
          onPageChange={setPage}
          fetchData={handleFetchData}
        />)
      )}
    </>
  );
};

export default BriefSummary;
